import React from 'react'
import { useNavigate } from 'react-router'
import styled from '@emotion/styled'
import { useQuery } from '@tanstack/react-query'
import { font, spacing, colors } from '@mobi/component-library/Theme/Common'
import { SvgLazy } from '@mobi/component-library/Common/Svg/SvgLazy'
import { Icon } from '@mobi/component-library/Common/Icon'
import { colors as mobiColors } from '@mobi/settings/colors'
import { AppRoutes } from '@core/AppRoutes'
import type { FollowMeProfile } from '@core/Areas/FollowMe/types'
import { ProfileAvatar } from '@core/Areas/FollowMe/Components/Common/ProfileAvatar'
import { FollowButton } from '@core/Areas/FollowMe/Components/Common/FollowButton'
import { followMePrincipleProfileQuery } from '@core/Areas/FollowMe/helpers/queries'

const pluralize = (count: number, singular: string, plural?: string) => {
  const label = count === 1 ? singular : (plural ?? `${singular}s`)
  return `${count} ${label}`
}

export const FollowProfileItem: React.FC<{
  followMeProfile: FollowMeProfile
}> = ({ followMeProfile }) => {
  const { data: profile } = useQuery(followMePrincipleProfileQuery)
  const navigate = useNavigate()

  const isMyProfile = profile?.id === followMeProfile.id

  const handleProfileClick = () => {
    if (isMyProfile) {
      navigate(AppRoutes.BetLoopMyProfile)
    } else {
      navigate(`${AppRoutes.BetLoopProfile}/${followMeProfile.id}`)
    }
  }

  return (
    <ContainerStyled onClick={handleProfileClick}>
      <div>
        <ProfileAvatar avatar={followMeProfile.avatar} color={followMeProfile.color} />
        <div>
          <span>{followMeProfile.displayName}</span>
          <span>{pluralize(followMeProfile.followerCount, 'Follower')}</span>
        </div>
      </div>


      <div onClick={e => e.stopPropagation()}>
        <div style={{ marginRight: '1rem' }}>
          {followMeProfile.type === 'ExpertTipster' && (
            <Icon type='tabtouchLogoCompact' size='2.5em' color={mobiColors.brand.primary} />
          )}

          {followMeProfile.type === 'GiddyUp' && (
            <SvgLazy name='GiddyUpLogo' color='#0051A0' width='5em' />
          )}
        </div>

        {!isMyProfile ? (
          <FollowButton
            accountId={followMeProfile.id}
            accountDisplayName={followMeProfile.displayName}
            isFollowing={followMeProfile.isFollowing}
            isNotificationsEnabled={followMeProfile.isNotificationsEnabled}
            location='follow-list'
          />
        ) : null}
      </div>
    </ContainerStyled>
  )
}

// ======
// Styles
// ======

const ContainerStyled = styled.article({
  display: 'flex',
  flexDirection: 'row',
  padding: spacing.sm,
  backgroundColor: colors.white,
  fontFamily: font.family.primary,
  justifyContent: 'space-between',
  borderBottom: `1px solid ${colors.neutral[200]}`,
  WebkitTapHighlightColor: 'transparent',
  transition: 'background-color 0.15s ease',

  '@media (hover: hover)': {
    ':hover': { backgroundColor: colors.neutral[100], cursor: 'pointer' },
  },

  '&:active': {
    backgroundColor: colors.neutral[300],
  },

  '> div:first-of-type': {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.smx1,
    background: 0,
    border: 0,
    padding: 0,
    cursor: 'pointer',
    opacity: 1,
    transition: 'opacity 0.25s ease',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    minWidth: 0,

    '> div': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',

      '> span:first-of-type': {
        fontSize: font.size.lg.fontSize,
        fontWeight: font.weight.medium,
        lineHeight: font.size.lg.lineHeight,
        color: colors.black,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },

      '> span:last-of-type': {
        fontSize: font.size.md.fontSize,
        fontWeight: font.weight.regular,
        lineHeight: font.size.md.lineHeight,
        color: colors.neutral[800],
      },
    },
  },
  '> div:last-of-type': {
    display: 'flex',
    alignItems: 'center',

    '> button:first-of-type': {
      marginRight: '1rem',
    },
  },
})
